import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { tendersAPI, bidAgentsAPI, pdfAPI, forgeAPI } from '../services/api';
import { useBookmarks } from '../contexts/BookmarkContext';
import { useAnalysis } from '../contexts/AnalysisContext';
import {
  SummaryTab,
  HistoricalsTab,
  SourcingTab,
  BidPrepTab,
  FinancialTab,
  ComplianceTab,
  TAB_CONFIG
} from '../components/TenderDetailTabs';
import './TenderDetailAugmented.css';
import '../components/TenderDetailTabs/TenderDetailTabs.css';

// Phase mapping for tab-to-phase association
const TAB_PHASE_MAP = {
  summary: 'phase1',
  historicals: 'phase2',
  sourcing: 'phase3',
  bidprep: 'phase4',
  financial: 'phase2b',
  compliance: 'phase1',
};

// Maps each tab to the API phase endpoint(s) it should re-run
const TAB_RERUN_PHASES = {
  summary: ['1'],
  historicals: ['2'],
  sourcing: ['3'],
  bidprep: ['4'],
  financial: ['2b'],
  compliance: ['1'],
};

const ResponseDetail = () => {
  const { id: encodedId } = useParams();
  const id = decodeURIComponent(encodedId);
  const navigate = useNavigate();
  const { bookmarks, updateBookmarkFields } = useBookmarks();
  const { analysis, startAnalysis, initAnalysis } = useAnalysis(id);

  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');

  // PDF viewer state
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  // Document upload state
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  // Export report state
  const [exporting, setExporting] = useState(false);

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const chatMessagesRef = useRef(null);

  // Derive analysis state from context
  const analysisStatus = analysis.status;
  const completedPhases = analysis.completedPhases;
  const runningPhases = new Set(analysis.runningPhases);
  const failedPhases = new Set(analysis.failedPhases);
  const phases = analysis.phases;
  const analysisStartTime = analysis.startedAt;

  // On mount: fetch tender data and initialize analysis state from backend
  useEffect(() => {
    fetchTender();
    initAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Update bookmark when analysis completes — persist lightweight flags only
  useEffect(() => {
    if (analysisStatus === 'complete' && Object.keys(phases).length > 0) {
      const bidRec = phases.phase1?.bid_recommendation ||
                     phases.phase2?.bid_recommendation || null;
      updateBookmarkFields(id, {
        analysisStatus: 'complete',
        analyzedAt: new Date().toISOString(),
        bidRecommendation: bidRec,
        pipelineStage: 'bid_decision',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisStatus, phases]);

  const fetchTender = async () => {
    setLoading(true);
    try {
      const tenderResponse = await tendersAPI.getTender(id);
      setTender(tenderResponse);
    } catch (error) {
      console.error('Error fetching tender:', error);
    } finally {
      setLoading(false);
    }
  };

  // Re-run a specific phase for the active tab
  const [rerunning, setRerunning] = useState(null);
  const [rerunPhases, setRerunPhases] = useState({});
  const [rerunningAll, setRerunningAll] = useState(false);
  const rerunPhase = useCallback(async (tabId) => {
    const phaseKeys = TAB_RERUN_PHASES[tabId];
    if (!phaseKeys) return;

    setRerunning(tabId);
    try {
      for (const phaseKey of phaseKeys) {
        const result = await bidAgentsAPI.runPhase(phaseKey, id, true);
        const stateKey = phaseKey === '4' ? 'phase4' : `phase${phaseKey}`;
        setRerunPhases(prev => ({ ...prev, [stateKey]: result }));
      }
    } catch (err) {
      console.error(`Error re-running phase for ${tabId}:`, err);
    } finally {
      setRerunning(null);
    }
  }, [id]);

  // Clear cache and rerun entire analysis
  const rerunAll = useCallback(async () => {
    setRerunningAll(true);
    try {
      await bidAgentsAPI.invalidateCache(id);
      setRerunPhases({});
      await startAnalysis();
    } catch (err) {
      console.error('Error rerunning full analysis:', err);
    } finally {
      setRerunningAll(false);
    }
  }, [id, startAnalysis]);

  // Merge context phases with any locally re-run phases
  const mergedPhases = { ...phases, ...rerunPhases };

  const formatCurrency = (value) => {
    const amount = parseFloat(value || 0);
    if (amount === 0) return 'Not disclosed';
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M CAD`;
    }
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  const getDisplayStatus = (tender) => {
    const closingDate = tender.closing_date;
    const esStatus = (tender.status || '').toLowerCase();

    if (closingDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const closeDate = new Date(closingDate);
      closeDate.setHours(0, 0, 0, 0);

      if (closeDate < today) {
        if (esStatus === 'active') return 'Closing Date Passed';
        return 'Closed';
      }
    }
    return tender.status || 'Open';
  };

  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'closing date passed') return 'status-closed';
    if (statusLower.includes('active') || statusLower.includes('open')) return 'status-open';
    if (statusLower.includes('closed') || statusLower.includes('expired')) return 'status-closed';
    if (statusLower.includes('awarded')) return 'status-awarded';
    return 'status-default';
  };

  const getPlatformName = (tender) => {
    const url = tender?.external_platform_url || tender?.notice_url || '';
    const urlLower = url.toLowerCase();
    if (urlLower.includes('bonfirehub.com') || urlLower.includes('gobonfire.com')) return 'Bonfire';
    if (urlLower.includes('merx.com')) return 'MERX';
    if (urlLower.includes('ariba.com') || urlLower.includes('sap.com')) return 'SAP Ariba';
    if (urlLower.includes('purchasingconnection.ca') || urlLower.includes('alberta.ca')) return 'Alberta Purchasing';
    if (urlLower.includes('bidsandtenders.ca')) return 'Bids & Tenders';
    return 'CanadaBuys';
  };

  const getFileIcon = (fileType) => {
    const icons = { pdf: '📄', image: '🖼️', spreadsheet: '📊' };
    return icons[fileType] || '📄';
  };

  const handlePdfView = async (pdfUrl) => {
    try {
      const response = await fetch(`/api/pdfs/view?url=${encodeURIComponent(pdfUrl)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.type === 'external') {
          window.open(data.url, '_blank');
          return;
        }
        setLoadingPdf(true);
        setShowPdfViewer(true);
        setCurrentPdfUrl(data.url);
        setLoadingPdf(false);
      }
    } catch (error) {
      console.error('Error loading PDF:', error);
    }
  };

  const handleDocumentView = async (doc) => {
    const fileType = doc.file_type || 'pdf';
    if (fileType === 'image' || fileType === 'spreadsheet') {
      try {
        const response = await fetch(`/api/pdfs/view?url=${encodeURIComponent(doc.url)}`);
        if (response.ok) {
          const data = await response.json();
          window.open(data.url, '_blank');
        }
      } catch {
        // silently fail
      }
      return;
    }
    handlePdfView(doc.url);
  };

  // Document upload handler
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError(null);
    try {
      await pdfAPI.uploadDocument(id, file);
      // Refresh tender data to show the new document
      const tenderResponse = await tendersAPI.getTender(id);
      setTender(tenderResponse);
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      // Reset file input so the same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Chat handlers
  const handleChatSend = async (text) => {
    const message = text || chatInput.trim();
    if (!message || chatLoading) return;

    const userMsg = { role: 'user', content: message };
    const updatedMessages = [...chatMessages, userMsg];
    setChatMessages(updatedMessages);
    setChatInput('');
    setChatLoading(true);

    try {
      const history = chatMessages.map(m => ({ role: m.role, content: m.content }));
      const { reply } = await bidAgentsAPI.sendChatMessage(id, message, history);
      setChatMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your question. Please try again.'
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSend();
    }
  };

  // Export PDF report
  const handleExportReport = async () => {
    setExporting(true);
    try {
      const blob = await bidAgentsAPI.exportReport(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BidAnalysis_${(tender?.solicitation_number || id).replace(/[/ ]/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages, chatLoading]);

  const CHAT_SUGGESTIONS = [
    'What are the key risks?',
    'Summarize the product requirements',
    'What is the profit margin opportunity?',
    'What certifications are needed?',
  ];

  // Check if a tab's required phase is available
  const isTabPhaseReady = (tabId) => {
    const requiredPhase = TAB_PHASE_MAP[tabId];
    if (!requiredPhase) return true;
    return !!mergedPhases[requiredPhase];
  };

  const isTabPhaseRunning = (tabId) => {
    const requiredPhase = TAB_PHASE_MAP[tabId];
    return runningPhases.has(requiredPhase);
  };

  const isTabPhaseFailed = (tabId) => {
    const requiredPhase = TAB_PHASE_MAP[tabId];
    return failedPhases.has(requiredPhase);
  };

  // Estimate remaining time based on elapsed and phases completed
  const getEstimatedTimeRemaining = () => {
    if (!analysisStartTime || analysisStatus !== 'running') return null;
    const elapsed = (Date.now() - analysisStartTime) / 1000;
    const done = completedPhases.length;
    if (done <= 1) {
      const remaining = Math.max(0, 360 - elapsed);
      return Math.ceil(remaining / 60);
    }
    const parallelDone = done - 2;
    const parallelTotal = 5;
    if (parallelDone >= parallelTotal) return 0;
    const parallelElapsed = elapsed - 80;
    if (parallelDone > 0 && parallelElapsed > 0) {
      const perPhase = parallelElapsed / parallelDone;
      const remaining = perPhase * (parallelTotal - parallelDone);
      return Math.max(1, Math.ceil(remaining / 60));
    }
    return Math.max(1, Math.ceil((300 - elapsed) / 60));
  };

  // Render tab content with phase data
  const renderTabContent = () => {
    const analysisPhaseKeys = Object.keys(mergedPhases).filter(k => k.startsWith('phase'));
    if (analysisStatus === 'idle' && analysisPhaseKeys.length === 0) {
      return (
        <div className="analysis-cta">
          <div className="cta-icon">&#x1F50D;</div>
          <h3>No Analysis Yet</h3>
          <p>Run AI analysis to get qualification insights, competitive intelligence, sourcing strategies, and bid preparation guidance.</p>
          <button className="btn-cta" onClick={startAnalysis}>
            Run Analysis
          </button>
        </div>
      );
    }

    if (analysisStatus === 'running' && !isTabPhaseReady(activeTab)) {
      return (
        <div className="tab-pending-placeholder">
          <div className="placeholder-spinner"></div>
          <p>Results will appear here automatically</p>
        </div>
      );
    }

    const phase1 = mergedPhases.phase1;
    const phase2 = mergedPhases.phase2;
    const phase2b = mergedPhases.phase2b;
    const phase2c = mergedPhases.phase2c;
    const phase3 = mergedPhases.phase3;
    const phase4 = mergedPhases.phase4;

    switch (activeTab) {
      case 'summary':
        return (
          <SummaryTab
            tender={tender}
            bidAssessment={null}
            phase1Data={phase1}
            phase2Data={phase2}
            phase2bData={phase2b}
            phase2cData={phase2c}
            qualification={phase1}
            qualificationLoading={false}
            loading={false}
          />
        );
      case 'historicals':
        return (
          <HistoricalsTab
            tender={tender}
            phase2Data={phase2}
            loading={false}
          />
        );
      case 'sourcing':
        return <SourcingTab tender={tender} phase1Data={phase1} phase2Data={phase2} phase2bData={phase2b} phase2cData={phase2c} phase3Data={phase3} />;
      case 'bidprep':
        return <BidPrepTab tender={tender} phase4Data={phase4} />;
      case 'financial':
        return <FinancialTab tender={tender} phase1Data={phase1} phase2Data={phase2} phase2bData={phase2b} phase2cData={phase2c} />;
      case 'compliance':
        return <ComplianceTab tender={tender} phase1Data={phase1} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="tender-detail-augmented">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading response details...</p>
        </div>
      </div>
    );
  }

  if (!tender) {
    return (
      <div className="tender-detail-augmented">
        <div className="error-container">
          <h2>Response not found</h2>
          <p>Could not load tender data for this response.</p>
          <button onClick={() => navigate('/my-tenders')} className="btn-primary">
            Back to My Tenders
          </button>
        </div>
      </div>
    );
  }

  const bi = tender.business_intelligence || {};
  const org = typeof tender.organization === 'object' ? tender.organization : {};
  const orgName = typeof tender.organization === 'string'
    ? tender.organization
    : (org.name || org.contracting_entity || 'N/A');
  const displayStatus = getDisplayStatus(tender);
  const statusClass = getStatusColor(displayStatus);

  const docCount = (tender.discovered_content?.documents?.length || 0) +
                   (tender.discovered_pdfs?.length || 0) +
                   (tender.documents?.length || 0);

  return (
    <div className="tender-detail-augmented">
      <div className="container">
        {/* Back Button */}
        <button onClick={() => navigate('/my-tenders')} className="back-button">
          &#x2190; Back to My Tenders
        </button>

        {/* Header Card */}
        <div className="header-card-new">
          <div className="header-top-row">
            <div className="header-title-section">
              <h1 className="tender-title-new">{tender.title}</h1>
              <span className={`status-badge-new ${statusClass}`}>
                {displayStatus}
              </span>
            </div>
            <div className="header-actions-new">
              {/* This page only renders for analysed tenders → always offer FORGE */}
              <button
                className="btn-send-forge"
                onClick={() => { forgeAPI.enterForge(id, tender.title || '').catch(() => {}); navigate(`/forge?tender=${encodeURIComponent(id)}`); }}
                title="Build the bid in FORGE"
              >
                Send to FORGE &rarr;
              </button>
              {Object.keys(mergedPhases).some(k => k.startsWith('phase')) && (
                <button
                  className="btn-secondary"
                  onClick={handleExportReport}
                  disabled={exporting}
                >
                  {exporting ? 'Exporting...' : 'Export Report'}
                </button>
              )}
              {(tender.external_platform_url || tender.notice_url) && (
                <a
                  href={tender.external_platform_url || tender.notice_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                >
                  View on {getPlatformName(tender)}
                </a>
              )}
            </div>
          </div>

          <div className="meta-pills">
            <div className="meta-pill">
              <span className="pill-label">Est. Amount</span>
              <span className="pill-value">{formatCurrency(tender.value?.amount)}</span>
            </div>
            <div className="meta-pill">
              <span className="pill-label">Closing Date</span>
              <span className="pill-value">{formatDate(tender.closing_date)}</span>
            </div>
            <div className="meta-pill">
              <span className="pill-label">Buyer</span>
              <span className="pill-value">{orgName}</span>
            </div>
            <div className="meta-pill">
              <span className="pill-label">Category</span>
              <span className="pill-value">
                {bi.master_category || tender.notice_type || 'General'}
              </span>
            </div>
          </div>
        </div>

        {/* Tab Navigation Bar */}
        <div className="tabs-nav">
          <div className="tabs-list">
            {TAB_CONFIG.map(tab => {
              const phaseReady = isTabPhaseReady(tab.id);
              const phaseRunning = isTabPhaseRunning(tab.id);
              const phaseFailed = isTabPhaseFailed(tab.id);
              const isRunning = analysisStatus === 'running';
              let phaseState = '';
              if (isRunning) {
                if (phaseFailed) phaseState = 'tab-failed';
                else if (phaseReady) phaseState = 'tab-done';
                else if (phaseRunning) phaseState = 'tab-running';
                else phaseState = 'tab-pending';
              }
              return (
                <button
                  key={tab.id}
                  className={`tab-button ${activeTab === tab.id ? 'active' : ''} ${tab.comingSoon ? 'coming-soon' : ''} ${phaseState}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                  {isRunning && phaseFailed && (
                    <span className="tab-status-icon tab-icon-failed">&#x2717;</span>
                  )}
                  {isRunning && phaseReady && !phaseFailed && (
                    <span className="tab-status-icon tab-icon-done">&#x2713;</span>
                  )}
                  {isRunning && phaseRunning && !phaseReady && (
                    <span className="tab-spinner"></span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Analysis status indicator */}
          {analysisStatus === 'running' && (
            <div className="tabs-analysis-status">
              <span className="analysis-running-indicator">
                <span className="pulse-dot"></span>
                Analyzing... {completedPhases.length}/7 phases
                {(() => {
                  const mins = getEstimatedTimeRemaining();
                  if (mins !== null && mins > 0) return ` · ~${mins} min left`;
                  return '';
                })()}
              </span>
            </div>
          )}
          {analysisStatus === 'failed' && (
            <div className="tabs-analysis-status">
              <span className="analysis-failed-indicator">
                Failed
                <button className="btn-retry-inline" onClick={startAnalysis}>Retry</button>
              </span>
            </div>
          )}
        </div>

        {/* Activity Log — visible during analysis */}
        {analysisStatus === 'running' && analysis.progress.length > 0 && (
          <div className="workspace-bar" style={{ display: 'block' }}>
            <div className="workspace-activity" style={{ width: '100%' }}>
              <label className="workspace-label">Activity</label>
              <div className="activity-log">
                {analysis.progress.slice(-15).map((entry, i) => (
                  <div key={i} className="activity-entry activity-info">
                    <span className="activity-time">
                      {new Date(entry.timestamp).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </span>
                    <span className="activity-phase">[{entry.phase}]</span>
                    <span className="activity-msg">{entry.message}</span>
                  </div>
                ))}
                <div className="activity-entry">
                  <span className="activity-cursor">
                    <span className="cursor-dot"></span>
                    <span className="cursor-dot"></span>
                    <span className="cursor-dot"></span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 3-Column Grid Layout */}
        <div className="tender-grid-new">
          {/* Left Column - Documents */}
          <div className="documents-panel">
            <div className="panel-header">
              <h3>Documents</h3>
              <span className="doc-count">{docCount} found</span>
            </div>

            <div className="doc-list">
              {tender.discovered_content?.documents?.map((doc, idx) => (
                <div key={`doc-${idx}`} className="doc-item">
                  <button onClick={() => handleDocumentView(doc)} className="doc-link">
                    {getFileIcon(doc.file_type)} {doc.title}
                  </button>
                </div>
              ))}

              {tender.discovered_pdfs?.map((pdf, idx) => (
                <div key={`pdf-${idx}`} className="doc-item">
                  <button onClick={() => handlePdfView(pdf.url)} className="doc-link">
                    📄 {pdf.title}
                  </button>
                </div>
              ))}

              {tender.documents?.map((doc, idx) => {
                try {
                  let docUrl = doc.url;
                  let docTitle = doc.filename;

                  if (typeof docUrl === 'string' && (docUrl.startsWith('[') || docUrl.startsWith('{'))) {
                    const parsed = JSON.parse(docUrl);
                    if (Array.isArray(parsed)) {
                      return parsed.map((pdfData, pdfIdx) => (
                        <div key={`${idx}-${pdfIdx}`} className="doc-item">
                          <button onClick={() => handlePdfView(pdfData.url)} className="doc-link">
                            📄 {pdfData.text || doc.filename}
                          </button>
                        </div>
                      ));
                    }
                    docUrl = parsed.url;
                    docTitle = parsed.text || doc.filename;
                  }

                  return (
                    <div key={idx} className="doc-item">
                      <button onClick={() => handlePdfView(docUrl)} className="doc-link">
                        📄 {docTitle}
                      </button>
                    </div>
                  );
                } catch {
                  return null;
                }
              })}

              {docCount === 0 && (
                <div className="doc-empty">
                  <p>No documents found.</p>
                </div>
              )}
            </div>

            {/* Upload Button */}
            <div className="doc-upload-section">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif,.gif,.bmp,.csv,.xlsx,.xls"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <button
                className="btn-upload-doc"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : '+ Upload Document'}
              </button>
              {uploadError && (
                <p className="upload-error">{uploadError}</p>
              )}
            </div>
          </div>

          {/* Center Column - Tab Content */}
          <div className="tab-content-panel">
            {/* Re-run buttons for completed analysis */}
            {analysisStatus !== 'running' && Object.keys(mergedPhases).some(k => k.startsWith('phase')) && (
              <div className="tab-rerun-bar">
                {TAB_RERUN_PHASES[activeTab] && (
                  <button
                    className="btn-rerun"
                    onClick={() => rerunPhase(activeTab)}
                    disabled={!!rerunning || rerunningAll}
                  >
                    {rerunning === activeTab ? (
                      <><span className="rerun-spinner"></span> Re-running...</>
                    ) : (
                      <><span className="rerun-icon">&#x21bb;</span> Re-run this phase</>
                    )}
                  </button>
                )}
                <button
                  className="btn-rerun btn-rerun-all"
                  onClick={rerunAll}
                  disabled={!!rerunning || rerunningAll}
                >
                  {rerunningAll ? (
                    <><span className="rerun-spinner"></span> Clearing cache...</>
                  ) : (
                    <><span className="rerun-icon">&#x21bb;</span> Rerun all phases</>
                  )}
                </button>
              </div>
            )}
            {renderTabContent()}
          </div>

          {/* Right Column - Key Details */}
          <div className="details-panel">
            <div className="kv-section">
              <h4>Key Details</h4>
              <div className="kv-item">
                <span className="kv-label">Solicitation #</span>
                <span className="kv-value">{tender.solicitation_number || tender.reference_number || 'N/A'}</span>
              </div>
              <div className="kv-item">
                <span className="kv-label">Region</span>
                <span className="kv-value">
                  {tender.regions_of_opportunity?.[0] || bi.delivery_regions?.[0] || 'Canada'}
                </span>
              </div>
              <div className="kv-item">
                <span className="kv-label">Language</span>
                <span className="kv-value">{tender.language || 'English'}</span>
              </div>
              <div className="kv-item">
                <span className="kv-label">Method</span>
                <span className="kv-value">{tender.procurement_method || 'Competitive'}</span>
              </div>
              {(tender.gsin_codes?.length > 0 || tender.gsin_description) && (
                <div className="kv-item">
                  <span className="kv-label">GSIN</span>
                  <span className="kv-value">
                    {tender.gsin_codes?.join(', ') || 'N/A'}
                    {tender.gsin_description && (
                      <span className="kv-sub">{tender.gsin_description}</span>
                    )}
                  </span>
                </div>
              )}
              {tender.unspsc_codes?.length > 0 && (
                <div className="kv-item">
                  <span className="kv-label">UNSPSC</span>
                  <span className="kv-value">
                    {tender.unspsc_codes.join(', ')}
                    {tender.unspsc_descriptions?.length > 0 && (
                      <span className="kv-sub">{tender.unspsc_descriptions[0]}</span>
                    )}
                  </span>
                </div>
              )}
            </div>

            <div className="kv-section">
              <h4>Contact</h4>
              <div className="kv-item">
                <span className="kv-label">Authority</span>
                <span className="kv-value">{orgName}</span>
              </div>
              {org.contact_name && (
                <div className="kv-item">
                  <span className="kv-label">Name</span>
                  <span className="kv-value">{org.contact_name}</span>
                </div>
              )}
              {org.contact_email && (
                <div className="kv-item">
                  <span className="kv-label">Email</span>
                  <span className="kv-value">
                    <a href={`mailto:${org.contact_email}`}>{org.contact_email}</a>
                  </span>
                </div>
              )}
            </div>

            {tender.trade_agreements?.length > 0 && (
              <div className="kv-section">
                <h4>Trade Agreements</h4>
                <div className="tag-list-new">
                  {tender.trade_agreements.map((agreement, i) => (
                    <span key={i} className="tag-new">{agreement}</span>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Floating Chat Widget */}
      <div className={`chat-widget ${chatOpen ? 'chat-widget--open' : ''}`}>
        {chatOpen && (
          <div className="chat-widget-window">
            <div className="chat-widget-header">
              <div className="chat-widget-header-left">
                <div className="chat-widget-avatar">AI</div>
                <div>
                  <div className="chat-widget-title">Tender Analyst</div>
                  <div className="chat-widget-subtitle">Ask anything about this tender</div>
                </div>
              </div>
              <button className="chat-widget-close" onClick={() => setChatOpen(false)}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            </div>

            <div className="chat-widget-body" ref={chatMessagesRef}>
              {chatMessages.length === 0 && !chatLoading && (
                <div className="chat-widget-empty">
                  <div className="chat-widget-empty-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="#635bff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <p className="chat-widget-empty-title">How can I help?</p>
                  <p className="chat-widget-empty-desc">I've analyzed this tender. Ask me about risks, requirements, pricing, or anything else.</p>
                  <div className="chat-widget-chips">
                    {CHAT_SUGGESTIONS.map((s, i) => (
                      <button key={i} className="chat-widget-chip" onClick={() => handleChatSend(s)}>{s}</button>
                    ))}
                  </div>
                </div>
              )}

              {chatMessages.map((msg, i) => (
                <div key={i} className={`chat-widget-msg chat-widget-msg--${msg.role}`}>
                  {msg.role === 'assistant' && <div className="chat-widget-msg-avatar">AI</div>}
                  <div className="chat-widget-msg-bubble">
                    {msg.role === 'assistant' ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    ) : (
                      msg.content
                    )}
                  </div>
                </div>
              ))}

              {chatLoading && (
                <div className="chat-widget-msg chat-widget-msg--assistant">
                  <div className="chat-widget-msg-avatar">AI</div>
                  <div className="chat-widget-msg-bubble chat-widget-typing">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
            </div>

            <div className="chat-widget-footer">
              <input
                type="text"
                className="chat-widget-input"
                placeholder="Type a message..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={handleChatKeyDown}
                disabled={chatLoading}
                autoFocus
              />
              <button
                className="chat-widget-send"
                onClick={() => handleChatSend()}
                disabled={chatLoading || !chatInput.trim()}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 2L15 22l-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </div>
          </div>
        )}

        <button
          className={`chat-widget-fab ${chatOpen ? 'chat-widget-fab--hidden' : ''}`}
          onClick={() => setChatOpen(true)}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          {chatMessages.length > 0 && (
            <span className="chat-widget-fab-badge">{chatMessages.length}</span>
          )}
        </button>
      </div>

      {/* PDF Viewer Modal */}
      {showPdfViewer && (
        <div className="modal-overlay pdf-viewer-overlay" onClick={() => setShowPdfViewer(false)}>
          <div className="modal-content pdf-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>PDF Viewer</h3>
              <button className="modal-close" onClick={() => setShowPdfViewer(false)}>&times;</button>
            </div>
            <div className="pdf-viewer-container">
              {loadingPdf ? (
                <div className="pdf-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading PDF...</p>
                </div>
              ) : currentPdfUrl ? (
                <iframe src={currentPdfUrl} title="PDF Viewer" className="pdf-iframe" />
              ) : (
                <div className="pdf-error">
                  <p>Failed to load PDF.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResponseDetail;
